import {
	Button,
	DragDrop,
	Dropdown,
	type DropdownItemProps,
	Grid,
	Header,
	Icon,
	Loader,
	Modal,
	Popup,
	Segment,
} from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import { isEmpty, isNil, omit } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type NotePresentation from '../../../interfaces/simulation/notePresentation';
import {
	getAllNotePresentation,
	restoreNotePresentation,
	updateNotePresentation,
} from '../../../services/simulation/notePresentation';
import Block from './Block/Block';
import './NotePresentationListe.css';
import type LigneNotePresentation from '../../../interfaces/simulation/ligneNotePresentation';
import UpdateModal from './Block/Modal/UpdateModal';

interface NotePresentationProps {
	onModelChange: (model: string) => void;
}

const defaultConfigLignes = {
	blockCode: ['Titre', 'Sous-titre', 'Paragraphe'],
	ContenuCourt: [
		'Titre de la note de présentation ',
		'Sous-titre de la note de présentation ',
		'Paragraphe de la note de présentation ',
	],
};

const NotePresentationListe = ({ onModelChange }: NotePresentationProps) => {
	const [currentTab, setCurrentTab] = React.useState<string>(null);

	const [options, setOptions] = React.useState<DropdownItemProps[]>([]);

	const [isRestore, setIsRestore] = React.useState(false);

	const [listeNotePresentation, setListeNotePresentation] = React.useState<
		NotePresentation[]
	>([]);

	const [loadingStates, setLoadingStates] = React.useState({
		listeNotePresentation: false,
		liste: false,
		modale: false,
	});

	const setLoadingListeNotePresentation = (value: boolean) => {
		setLoadingStates((prevState) => ({
			...prevState,
			listeNotePresentation: value,
		}));
	};

	const setLoadingListe = (value: boolean) => {
		setLoadingStates((prevState) => ({ ...prevState, liste: value }));
	};

	const setLoadingModale = (value: boolean) => {
		setLoadingStates((prevState) => ({ ...prevState, modale: value }));
	};

	const [notePresentationSelected, setNotePresentationSelected] =
		React.useState<NotePresentation>(null);

	const [isNoteModified, setIsNoteModified] = React.useState(false);

	const [ligneSelected, setLigneSelected] =
		React.useState<LigneNotePresentation>(null);

	const [ligneASupprimer, setLigneASupprimer] =
		React.useState<LigneNotePresentation>(null);

	const ordonnerLigne = (lignes: LigneNotePresentation[]) =>
		[...lignes].sort((a, b) => a.ordre - b.ordre);

	const handleIfModified = (note: NotePresentation) => {
		const confBlock = defaultConfigLignes.blockCode;
		const confContenuCourt = defaultConfigLignes.ContenuCourt;

		if (!note?.lignes) return false;
		if (note.lignes.length !== confBlock.length) return false;

		const lignesTriees = ordonnerLigne(note.lignes);

		if (
			!lignesTriees.every(
				(ligne, idx) => ligne.codeBlock.trimEnd() === confBlock[idx].trimEnd(),
			)
		)
			return false;

		if (
			!lignesTriees.every(
				(ligne, idx) =>
					ligne.contenuCourt?.trimEnd() === confContenuCourt[idx].trimEnd(),
			)
		)
			return false;

		if (!lignesTriees.every((ligne) => isEmpty(ligne.contenuLong)))
			return false;

		if (!lignesTriees.every((ligne) => isEmpty(ligne.titre))) return false;

		return true;
	};

	const fetchNotePresentation = async () => {
		let data: NotePresentation[] | null = null;
		if (!currentTab) {
			try {
				setLoadingListeNotePresentation(true);
				setLoadingListe(true);
				const response = await getAllNotePresentation(
					new URLSearchParams({
						withLignes: 'true',
						all: 'true',
					}),
				);
				data = response.data;

				const liste = data
					.map((note) => {
						if (!isNil(note.lignes)) {
							if (note.lignes.length !== 0) {
								return {
									...note,
									lignes: ordonnerLigne(note.lignes),
									deletedLignes: [],
								};
							}
						}
						return null;
					})
					.filter((note) => note !== null);

				setListeNotePresentation(liste);

				const dropdownOptions = liste.map((element) => ({
					key: element.libelle,
					text: element.libelle,
					value: element.libelle,
				}));
				setOptions(dropdownOptions);

				if (liste.length > 0) {
					setCurrentTab(liste[0].libelle);
					setNotePresentationSelected(liste[0]);
					setIsNoteModified(!handleIfModified(liste[0]));
				}
			} catch (_e) {
				toast.error('Impossible de charger les notes de présentations');
			} finally {
				setLoadingListeNotePresentation(false);
				setLoadingListe(false);
			}
		}
	};

	const handleDropdownChange = (_e, { value }) => {
		setCurrentTab(value);
		onModelChange(value);

		const selectedNote = listeNotePresentation.find(
			(note) => note.libelle === value,
		);
		if (selectedNote) {
			setNotePresentationSelected(selectedNote);
			setIsNoteModified(!handleIfModified(selectedNote));
		}
	};

	const handleUpdateLigne = (ligne: LigneNotePresentation) => {
		setLigneSelected(ligne);
	};

	const handleClose = () => {
		setLigneSelected(null);
		setLigneASupprimer(null);
		setIsRestore(false);
	};

	const handleDeleteLigne = (ligne: LigneNotePresentation) => {
		setLigneASupprimer(ligne);
	};

	const handleConfirmDeleteLigne = async () => {
		const { identifiant, ordre: ordreLigneDeleted } = ligneASupprimer;
		const lignesOrdered = notePresentationSelected.lignes
			.filter((ligne) => ligne.identifiant !== identifiant)
			.map((ligne) =>
				ligne.ordre > ordreLigneDeleted
					? { ...ligne, ordre: ligne.ordre - 1 }
					: ligne,
			);
		try {
			setLoadingListe(true);
			const nouvelleNote = await updateNotePresentation({
				identifiant: notePresentationSelected.identifiant,
				identifiantProduit: notePresentationSelected.identifiantProduit,
				libelle: notePresentationSelected.libelle,
				etapeBudgetaire: notePresentationSelected.etapeBudgetaire,
				deletedLignes: [identifiant],
				lignes: [...lignesOrdered.filter((l) => l.ordre >= ordreLigneDeleted)],
			});
			setNotePresentationSelected({ ...nouvelleNote, lignes: lignesOrdered });
			setIsNoteModified(
				!handleIfModified({ ...nouvelleNote, lignes: lignesOrdered }),
			);

			const updatedListe = listeNotePresentation.map((note) =>
				note.identifiant === nouvelleNote.identifiant ? nouvelleNote : note,
			);
			setListeNotePresentation(updatedListe);
		} catch (e) {
			toast.error(getErrorMessage(e, "Le bloc n'a pas pu être supprimé."));
		} finally {
			setLoadingListe(false);
		}
		handleClose();
	};

	const handleSaveCreation = async (
		ligneNote: LigneNotePresentation,
		codeBlock: string,
	) => {
		const ordreLigne = ligneNote.ordre;
		const ligneMiseAJour = notePresentationSelected.lignes.map((ligneAJour) =>
			ligneAJour.ordre > ordreLigne
				? { ...ligneAJour, ordre: ligneAJour.ordre + 1 }
				: ligneAJour,
		);

		try {
			setLoadingListe(true);
			const nouvelleNote = await updateNotePresentation({
				identifiant: notePresentationSelected.identifiant,
				identifiantProduit: notePresentationSelected.identifiantProduit,
				libelle: notePresentationSelected.libelle,
				etapeBudgetaire: notePresentationSelected.etapeBudgetaire,
				lignes: [
					{
						codeBlock,
						ordre: ordreLigne + 1,
					},
					...ligneMiseAJour.filter((l) => l.ordre > ordreLigne),
				],
			});
			setNotePresentationSelected({ ...nouvelleNote });
			setIsNoteModified(!handleIfModified({ ...nouvelleNote }));

			const updatedListe = listeNotePresentation.map((note) =>
				note.identifiant === nouvelleNote.identifiant ? nouvelleNote : note,
			);
			setListeNotePresentation(updatedListe);
		} catch (e) {
			toast.error(getErrorMessage(e, "Le bloc n'a pas pu être créé."));
		} finally {
			setLoadingListe(false);
		}
		handleClose();
	};

	const handleSaveUpdate = async (ligne: LigneNotePresentation) => {
		try {
			setLoadingModale(true);
			setLoadingListe(true);
			const nouvelleNote = await updateNotePresentation({
				identifiant: notePresentationSelected.identifiant,
				identifiantProduit: notePresentationSelected.identifiantProduit,
				libelle: notePresentationSelected.libelle,
				etapeBudgetaire: notePresentationSelected.etapeBudgetaire,
				lignes: [ligne],
			});

			setNotePresentationSelected({ ...nouvelleNote });
			setIsNoteModified(!handleIfModified({ ...nouvelleNote }));

			const updatedListe = listeNotePresentation.map((note) =>
				note.identifiant === nouvelleNote.identifiant ? nouvelleNote : note,
			);
			setListeNotePresentation(updatedListe);

			const updatedLigne = nouvelleNote.lignes.find(
				(l) => l.identifiant === ligne.identifiant,
			);
			setLigneSelected(updatedLigne);
		} catch (e) {
			toast.error(getErrorMessage(e, "Le bloc n'a pas pu être modifié."));
		} finally {
			setLoadingModale(false);
			setLoadingListe(false);
		}
		handleClose();
	};

	const renderNode = (item) => {
		// Pendant un déplacement, DragDrop clone la ligne dans un portail et ne
		// transmet alors que la poignée : les deux formes de dragProps coexistent.
		const { draggable, dragHandle } = item.dragProps ?? {};

		return (
			<div className="ligne-note-presentation" {...(draggable || {})}>
				<Block
					key={item.identifiant}
					onAdd={handleSaveCreation}
					onUpdate={handleUpdateLigne}
					onDelete={handleDeleteLigne}
					ligne={omit(item, ['dragProps'])}
					dragHandle={dragHandle}
					isLastLigne={notePresentationSelected.lignes.length === 1}
				/>
			</div>
		);
	};

	const handleDragEnd = async (result) => {
		const lignes = [...notePresentationSelected.lignes];

		const sourceIndex = result.source.index + 1;
		const destinationIndex = result.destination.index + 1;

		const lignesDeplacee = lignes.filter(
			(ligne) =>
				(ligne.ordre < sourceIndex && ligne.ordre >= destinationIndex) ||
				(ligne.ordre > sourceIndex && ligne.ordre <= destinationIndex),
		);

		const lignesTriees = lignes.map((ligne, index) => {
			if (index === result.source.index) {
				return {
					...ligne,
					ordre: destinationIndex,
				};
			}
			if (lignesDeplacee.includes(ligne)) {
				return {
					...ligne,
					ordre: ligne.ordre > sourceIndex ? ligne.ordre - 1 : ligne.ordre + 1,
				};
			}
			return ligne;
		});

		const nouvelleNote = {
			...notePresentationSelected,
			lignes: [...lignesTriees],
		};
		try {
			setLoadingListe(true);
			await updateNotePresentation(nouvelleNote);
			setNotePresentationSelected(nouvelleNote);
			setIsNoteModified(!handleIfModified(nouvelleNote));
			const updatedListe = listeNotePresentation.map((note) =>
				note.identifiant === nouvelleNote.identifiant ? nouvelleNote : note,
			);
			setListeNotePresentation(updatedListe);
		} catch (e) {
			toast.error(getErrorMessage(e, "La note n'a pas pu être mise à jour."));
		} finally {
			setLoadingListe(false);
		}
	};

	const handleRestoreNotePresentation = async () => {
		if (notePresentationSelected?.identifiant) {
			try {
				setLoadingListeNotePresentation(true);
				const restoredNote = await restoreNotePresentation(
					notePresentationSelected.identifiant,
				);
				setNotePresentationSelected(restoredNote);
				setIsNoteModified(false);
				const updatedListe = listeNotePresentation.map((note) =>
					note.identifiant === restoredNote.identifiant ? restoredNote : note,
				);
				setListeNotePresentation(updatedListe);
			} catch (e) {
				toast.error(getErrorMessage(e, "La note n'a pas pu être restaurée."));
			} finally {
				setLoadingListeNotePresentation(false);
				setIsRestore(false);
			}
		}
	};

	const handleRestoreModale = async () => {
		setIsRestore(true);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'fetchNotePresentation'
	React.useEffect(() => {
		fetchNotePresentation();
	}, [onModelChange]);

	if (loadingStates?.listeNotePresentation) {
		return (
			<Segment basic padded="very">
				<Loader active inline="centered" />
			</Segment>
		);
	}

	if (listeNotePresentation.length === 0) {
		return (
			<Segment basic padded="very" textAlign="center">
				<Header
					as="h4"
					icon
					className="no-note-presentation"
					data-testid="aucuneNotePresentation"
				>
					<Icon
						className="icon"
						iconSet="Lucide"
						name="FileX2"
						color="grey"
						size={40}
					/>
					<Header.Content>
						Aucune note de présentation disponible
						<Header.Subheader>
							Aucune trame n&apos;est paramétrée pour cet exercice.
						</Header.Subheader>
					</Header.Content>
				</Header>
			</Segment>
		);
	}

	return (
		<Grid className="note-presentation">
			<Grid.Row verticalAlign="middle" className="note-presentation-entete">
				<Grid.Column width={13}>
					<Dropdown
						placeholder="Sélectionnez une note"
						fluid
						selection
						data-testid="selectionNotePresentation"
						options={options}
						value={currentTab}
						onChange={handleDropdownChange}
					/>
				</Grid.Column>
				<Grid.Column width={3} textAlign="right">
					{!isNil(notePresentationSelected) && isNoteModified && (
						<Popup
							inverted
							position="left center"
							content="Restaurer la note de présentation"
							data-testid="restoreNotePresentationPopup"
							trigger={
								<Button
									basic
									icon
									type="button"
									className="restauration-note"
									aria-label="Restaurer la note de présentation"
									data-testid="restoreNotePresentation"
									onClick={handleRestoreModale}
								>
									<Icon iconSet="Lucide" name="DatabaseBackup" size={18} />
								</Button>
							}
						/>
					)}
				</Grid.Column>
			</Grid.Row>
			<Grid.Row>
				<Grid.Column width={16}>
					{(loadingStates?.liste && (
						<Segment basic padded="very">
							<Loader active inline="centered" />
						</Segment>
					)) ||
						(notePresentationSelected.lignes && (
							<div className="lignes-note-presentation">
								<DragDrop
									items={ordonnerLigne(notePresentationSelected.lignes)}
									itemControl={renderNode}
									onDragEnd={handleDragEnd}
								/>
							</div>
						))}
				</Grid.Column>
			</Grid.Row>
			<UpdateModal
				onClose={handleClose}
				onSave={handleSaveUpdate}
				ligne={ligneSelected}
				loading={loadingStates?.modale}
				open={!isNil(ligneSelected)}
			/>
			<Modal
				size="tiny"
				open={!isNil(ligneASupprimer)}
				onClose={handleClose}
				data-testid="deleteBlockModal"
			>
				<Modal.Header>Supprimer le bloc</Modal.Header>
				<Modal.Content>
					Voulez-vous vraiment supprimer le bloc
					{ligneASupprimer && ` « ${ligneASupprimer.codeBlock} »`} ?
				</Modal.Content>
				<Modal.Actions>
					<Button
						content="Annuler"
						data-testid="cancelDeleteButton"
						onClick={handleClose}
					/>
					<Button
						content="Supprimer"
						data-testid="confirmDeleteButton"
						onClick={handleConfirmDeleteLigne}
						negative
					/>
				</Modal.Actions>
			</Modal>
			<Modal size="tiny" open={isRestore} onClose={handleClose}>
				<Modal.Header>Réinitialiser la note de présentation</Modal.Header>
				<Modal.Content>
					Voulez-vous vraiment restaurer la note d&apos;origine ?
				</Modal.Content>
				<Modal.Actions>
					<Button
						content="Annuler"
						data-testid="cancelButton"
						onClick={handleClose}
					/>
					<Button
						content="Réinitialiser"
						data-testid="confirmButton"
						onClick={handleRestoreNotePresentation}
						negative
					/>
				</Modal.Actions>
			</Modal>
		</Grid>
	);
};

export default NotePresentationListe;
